import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AstroSystem, DiscoveryCard, LocaleId, TreeProgress } from '@astrally/shared';
import {
  CONVERSATION_TREES,
  progressDepth,
  treeById,
  treeCompletion,
} from '@astrally/shared';
import {
  composeHoroscope,
  computeDayWindows,
  computePanchanga,
  getDefaultAdapter,
  type DayWindows,
  type HoroscopeResult,
  type Panchanga,
} from '@astrally/astro-engine';
import { useApp, type ChatEntry } from '../../state/AppState';
import { useI18n, type I18nKey } from '../../i18n';
import { localdb } from '../../lib/localdb';
import { Bubble } from '../ally/AllyBubble';
import { useAllySuggestions } from '../ally/suggestions';
import { InfoDot, initialsOf, Section } from '../../components/common';
import { Term } from '../../components/Term';
import { NorthIndianChart } from '../charts/NorthIndianChart';
import { LESSON_COUNT, type LessonHit } from '../learn/curriculum';
import { learnDoneCount, nextUnfinishedLesson, readLearnDone } from '../learn/LearnScreen';

/** Greeting keys exist so this line translates; it used to be literal English. */
function greetingKey(hour: number): I18nKey {
  if (hour < 12) return 'home.greeting.morning';
  if (hour < 17) return 'home.greeting.afternoon';
  if (hour < 22) return 'home.greeting.evening';
  return 'home.greeting.night';
}

/**
 * Date-formatting locales. India is the primary market, so even English users
 * get en-IN ("Thursday, 6 August") — the old toDateString() rendered the US
 * debug-looking "Thu Aug 06 2026".
 */
const DATE_LOCALES: Record<LocaleId, string> = {
  en: 'en-IN',
  hi: 'hi-IN',
  hinglish: 'en-IN',
  bn: 'bn-IN',
};

/** Longest preview of the last reply that still fits two lines on a phone. */
const CONTINUE_PREVIEW = 150;

/** How many deterministic signals each tradition card shows before "see all". */
const SIGNALS_ON_HOME = 2;

const SYSTEMS: AstroSystem[] = ['vedic', 'western'];

const SYSTEM_LABEL: Record<AstroSystem, I18nKey> = {
  vedic: 'system.vedic',
  western: 'system.western',
};

/**
 * Local calendar day. Deliberately not `toISOString().slice(0, 10)` — that is
 * UTC, so in IST the day would roll over at 5:30 am and burn a fresh message.
 */
function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Midnight-to-midnight window of the reader's own civil day, as UTC instants. */
function localDayWindow(d: Date): { fromUtc: Date; toUtc: Date } {
  const from = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const to = new Date(from.getTime());
  to.setDate(to.getDate() + 1);
  return { fromUtc: from, toUtc: to };
}

function truncate(text: string, max: number): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

/** Local wall-clock HH:MM of a UTC instant, for window times. */
function clock(d: Date, locale: LocaleId): string {
  return d.toLocaleTimeString(DATE_LOCALES[locale], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Where the reader is, as far as the app can honestly tell.
 *
 * Birth coordinates are the ONLY coordinates Astrally holds — onboarding asks
 * for a current city as free text, never a position — so sunrise, sunset and
 * the panchanga limbs are anchored there. The civil day itself comes from the
 * device timezone instead, because "today" has to mean the user's today even
 * if they emigrated. When the two disagree the day boundary is right and the
 * sunrise is stale by however far they moved; that is the better failure.
 */
interface Anchor {
  lat: number;
  lon: number;
  timezone: string;
}

/**
 * The natal Moon's sidereal sign — the single value the whole Vedic gochara
 * layer is counted from.
 *
 * `certain` is false when an approximate or unknown birth time lets the Moon
 * cross a sign boundary inside the window. We do NOT quietly read the
 * midpoint into a Vedic reading in that case: the entire reading would be
 * anchored on a coin flip. Cloud Functions refuse the same case with
 * `moon-sign-uncertain`; this mirrors it so the client and server never
 * disagree about what a user is entitled to see.
 */
interface MoonAnchor {
  sign: number | null;
  certain: boolean;
}

function natalMoonAnchor(app: ReturnType<typeof useApp>): MoonAnchor {
  const chart = app.chart;
  if (!chart) return { sign: null, certain: false };
  const exact = chart.positions?.find((p) => p.planet === 'moon');
  if (exact) return { sign: exact.sidereal.sign, certain: true };
  const varies = chart.uncertainty?.varying.some((f) => f.id === 'planet.moon.siderealSign');
  const midpoint = chart.uncertainty?.midpointPositions.find((p) => p.planet === 'moon');
  if (!midpoint) return { sign: null, certain: false };
  return { sign: midpoint.sidereal.sign, certain: !varies };
}

/**
 * The AI's job on this screen is to NARRATE what the engine already produced —
 * so the prompt carries the composed headline and the exact signals, and asks
 * for nothing beyond them. It never asks the model where a planet is.
 */
function narrationPrompt(readings: HoroscopeResult[], panchanga: Panchanga | null): string {
  const parts = readings.map((r) => {
    const lines = r.strongest
      .slice(0, 3)
      .map((s) => `- ${s.label}: ${s.meaning}`)
      .join('\n');
    return `${r.system === 'vedic' ? 'Vedic' : 'Western'} reading for today — ${r.headline}\n${lines}`;
  });
  const day = panchanga
    ? `\n\nToday's panchanga: ${panchanga.tithi.name} tithi (${panchanga.tithi.paksha}), ${panchanga.nakshatra.name} nakshatra, ${panchanga.yoga.name} yoga, ${panchanga.vaara.englishName}.`
    : '';
  return (
    'Read today for me. Astrally has already calculated everything below — put it together ' +
    'in your own words for someone with no astrology background, say what it MEANS rather ' +
    'than what will happen, and do not add any placement I have not given you.\n\n' +
    parts.join('\n\n') +
    day
  );
}

/**
 * A reading Home showed on a past day, kept so the accuracy loop has something
 * concrete to ask about ("yesterday's said X — did that land?").
 *
 * Written by Home, read by Home. The verdict itself is NOT recorded here: it
 * belongs to the journal/accuracy surface, which owns the aggregate. Home only
 * remembers whether it has already asked, so the prompt does not nag forever.
 */
interface ShownReading {
  system: AstroSystem;
  periodKey: string;
  headline: string;
  signalId: string;
  signalLabel: string;
  signalMeaning: string;
}

const SHOWN_PREFIX = 'homeShown:';
const ASKED_PREFIX = 'homeAsked:';

/** Yesterday's local calendar day. */
function yesterdayKey(d: Date): string {
  const y = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  y.setDate(y.getDate() - 1);
  return dayKey(y);
}

/**
 * Home — the daily surface, and the reason the tab is called "Today".
 *
 * Everything above the fold is DETERMINISTIC: both traditions' readings, the
 * five panchanga limbs and today's rahu kaal are computed by the engine on
 * this device and render instantly, offline, at zero cost. Nothing on this
 * screen starts a generation on mount. The only AI here is one explicit
 * button, whose reply is cached under `dailyInsight:<uid>:<day>` so reopening
 * Home never re-spends one of the ten free daily messages.
 */
export function HomeScreen() {
  const { t, locale } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [aligned, setAligned] = useState<DiscoveryCard[]>([]);
  const suggestions = useAllySuggestions();

  // `reading` is local rather than app.allyBusy so a generation started from
  // somewhere else never makes this card look like it is working.
  const [insight, setInsight] = useState<ChatEntry | null>(null);
  const [reading, setReading] = useState(false);
  const [openSources, setOpenSources] = useState<string | null>(null);
  const [rateAsk, setRateAsk] = useState<ShownReading | null>(null);
  const [trees, setTrees] = useState<TreeProgress[]>([]);
  const [keepLearning, setKeepLearning] = useState<{
    done: number;
    /** null once every lesson is finished — the card stays, the nudge goes. */
    next: LessonHit | null;
  } | null>(null);

  const now = useMemo(() => new Date(), []);
  const day = dayKey(now);

  const anchor = useMemo<Anchor | null>(() => {
    const place = app.birthProfile?.input.place;
    if (!place) return null;
    return {
      lat: place.lat,
      lon: place.lon,
      // Device timezone, not the birth timezone — see the Anchor doc comment.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || place.timezone,
    };
  }, [app.birthProfile]);

  const moon = useMemo(() => natalMoonAnchor(app), [app]);

  /**
   * Both traditions, computed locally. `composeHoroscope` samples a day at
   * four-hour steps, so this is seven position solves per system — a few
   * milliseconds, cheap enough to sit in a memo rather than a cache.
   */
  const readings = useMemo<HoroscopeResult[]>(() => {
    const chart = app.chart;
    if (!chart || !anchor || moon.sign === null) return [];
    const { fromUtc, toUtc } = localDayWindow(now);
    const out: HoroscopeResult[] = [];
    for (const system of SYSTEMS) {
      // An uncertain natal Moon sign makes the Vedic reading a guess, not a
      // reading. Western reads transits to the whole chart and survives.
      if (system === 'vedic' && !moon.certain) continue;
      try {
        out.push(
          composeHoroscope({
            chart,
            natalMoonSign: moon.sign,
            period: 'day',
            fromUtc,
            toUtc,
            system,
            lat: anchor.lat,
            lon: anchor.lon,
          }),
        );
      } catch {
        // A single tradition failing must not blank the other one.
      }
    }
    return out;
  }, [app.chart, anchor, moon.sign, moon.certain, now]);

  /** Today's five limbs plus rahu kaal — pure calendar maths, no chart needed. */
  const almanac = useMemo<{ panchanga: Panchanga; windows: DayWindows } | null>(() => {
    if (!anchor) return null;
    try {
      const adapter = getDefaultAdapter();
      return {
        panchanga: computePanchanga(now, anchor.lat, anchor.lon, adapter, app.ayanamsaId, {
          timezone: anchor.timezone,
        }),
        windows: computeDayWindows(now, anchor.lat, anchor.lon, adapter, {
          timezone: anchor.timezone,
        }),
      };
    } catch {
      return null;
    }
  }, [anchor, app.ayanamsaId, now]);

  /**
   * The D1 varga for the Home mini-kundli. The engine already computed it when
   * the chart was built, so this is a lookup, not a calculation. The North
   * style needs a lagna to rotate around; a window-mode chart has none, and
   * Home then links to Charts honestly instead of drawing a guessed diamond.
   */
  const homeKundli = useMemo(() => {
    const d1 = app.chart?.jyotish?.vargas.find((v) => v.varga === 'D1') ?? null;
    return d1 && d1.placements.some((p) => p.body === 'lagna') ? d1 : null;
  }, [app.chart]);

  const insightKey = app.uid ? `dailyInsight:${app.uid}:${day}` : null;

  // Restore today's reading if there is one. Reading the cache is the only
  // thing that happens on mount — never a generation.
  useEffect(() => {
    if (!insightKey) {
      setInsight(null);
      return;
    }
    let alive = true;
    localdb
      .get<ChatEntry>('kv', insightKey)
      .then((cached) => {
        if (alive) setInsight(cached ?? null);
      })
      .catch(() => undefined);
    // Yesterday's readings are dead weight, and they accrue one row per day
    // forever if nobody sweeps them.
    localdb
      .keys('kv')
      .then((keys) =>
        Promise.all(
          keys
            .filter((k) => k.startsWith('dailyInsight:') && !k.endsWith(`:${day}`))
            .map((k) => localdb.del('kv', k)),
        ),
      )
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [insightKey, day]);

  /**
   * Remember today's top signal, and surface yesterday's if it was never
   * rated. This is the whole accuracy loop's hook: a prediction you were never
   * shown again is a prediction nobody can score.
   */
  useEffect(() => {
    const uid = app.uid;
    if (!uid || readings.length === 0) return;
    let alive = true;
    const top = readings[0];
    const signal = top.strongest[0];
    if (!signal) return;
    const record: ShownReading = {
      system: top.system,
      periodKey: day,
      headline: top.headline,
      signalId: signal.id,
      signalLabel: signal.label,
      signalMeaning: signal.meaning,
    };
    void localdb.set('kv', `${SHOWN_PREFIX}${uid}:${day}`, record).catch(() => undefined);

    const yKey = yesterdayKey(now);
    Promise.all([
      localdb.get<ShownReading>('kv', `${SHOWN_PREFIX}${uid}:${yKey}`),
      localdb.get<string>('kv', `${ASKED_PREFIX}${uid}:${yKey}`),
    ])
      .then(([shown, asked]) => {
        if (alive) setRateAsk(shown && !asked ? shown : null);
      })
      .catch(() => undefined);

    // Two days is all the loop needs; anything older is never asked about.
    localdb
      .keys('kv')
      .then((keys) =>
        Promise.all(
          keys
            .filter(
              (k) =>
                (k.startsWith(SHOWN_PREFIX) || k.startsWith(ASKED_PREFIX)) &&
                !k.endsWith(`:${day}`) &&
                !k.endsWith(`:${yKey}`),
            )
            .map((k) => localdb.del('kv', k)),
        ),
      )
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [app.uid, readings, day, now]);

  /**
   * Tree progress, read from the local mirror the Ally tree screen keeps under
   * `treeProgress:<uid>:<treeId>`. Absent simply means "not started yet", and
   * the card then invites the user into the first tree — so this degrades to
   * something correct rather than something wrong.
   */
  useEffect(() => {
    const uid = app.uid;
    if (!uid) {
      setTrees([]);
      return;
    }
    let alive = true;
    const prefix = `treeProgress:${uid}:`;
    localdb
      .keys('kv')
      .then((keys) =>
        Promise.all(keys.filter((k) => k.startsWith(prefix)).map((k) => localdb.get<TreeProgress>('kv', k))),
      )
      .then((rows) => {
        if (alive) setTrees(rows.filter((r): r is TreeProgress => !!r && Array.isArray(r.completedNodeIds)));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [app.uid]);

  /**
   * "Learn" — a purely local read of curriculum progress, zero AI.
   * ALWAYS visible: the people who most need the curriculum are exactly the
   * ones who have not started it, so hiding it from them was backwards, and a
   * reader who finished it still has a reference book to go back to. Zero
   * progress renders as an invitation, not a continuation.
   */
  useEffect(() => {
    let alive = true;
    readLearnDone()
      .then((set) => {
        if (!alive) return;
        setKeepLearning({ done: learnDoneCount(set), next: nextUnfinishedLesson(set) ?? null });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  /** The tree to invite the user back into: furthest along but not finished. */
  const nextTree = useMemo(() => {
    const started = trees
      .map((p) => {
        const tree = treeById(p.treeId);
        return tree ? { tree, progress: p, done: treeCompletion(tree, p) } : null;
      })
      .filter((x): x is { tree: (typeof CONVERSATION_TREES)[number]; progress: TreeProgress; done: number } => !!x)
      .filter((x) => x.done < 1)
      .sort((a, b) => b.done - a.done);
    if (started.length > 0) return started[0];
    const untouched = CONVERSATION_TREES.find((tree) => !trees.some((p) => p.treeId === tree.id));
    return untouched ? { tree: untouched, progress: null, done: 0 } : null;
  }, [trees]);

  const readToday = async () => {
    if (!insightKey || reading || app.allyBusy) return;
    setReading(true);
    // sendToAlly only rejects on its own busy guard — a backend failure comes
    // back as an ally message, which is exactly what we want to render.
    const entry = await app
      .sendToAlly(narrationPrompt(readings, almanac?.panchanga ?? null))
      .catch(() => null);
    setReading(false);
    if (!entry) return;
    setInsight(entry);
    await localdb.set('kv', insightKey, entry).catch(() => undefined);
  };

  /** Drops today's cache so the card can be generated once more. */
  const retryToday = async () => {
    if (!insightKey || reading || app.allyBusy) return;
    await localdb.del('kv', insightKey).catch(() => undefined);
    setInsight(null);
    await readToday();
  };

  /** Mark yesterday as asked-about, whichever way the user answered the prompt. */
  const closeRateAsk = async (then?: () => void) => {
    const uid = app.uid;
    const ask = rateAsk;
    setRateAsk(null);
    if (uid && ask) {
      await localdb.set('kv', `${ASKED_PREFIX}${uid}:${ask.periodKey}`, 'asked').catch(() => undefined);
    }
    then?.();
  };

  /**
   * Start the generation here, then navigate: by the time Ally mounts the
   * answer is already in flight, instead of the user landing on a prefilled
   * box and having to press send a second time.
   */
  const askAndOpen = (q: string) => {
    // A turn already in flight owns the screen; just go watch it.
    if (!app.allyBusy) app.sendToAlly(q).catch(() => undefined);
    navigate('/ai');
  };

  const socialOn = app.user?.socialEnabled === true;

  useEffect(() => {
    if (!socialOn) {
      setAligned([]);
      return;
    }
    app.backend
      .discoveryFeed()
      .then((r) => setAligned(r.cards.slice(0, 3)))
      .catch(() => setAligned([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.uid, app.entitlement?.tier, socialOn]);

  const lastAlly = useMemo(
    () => [...app.chatHistory].reverse().find((e) => e.role === 'ally'),
    [app.chatHistory],
  );

  const dateLabel = useMemo(
    () =>
      new Date().toLocaleDateString(DATE_LOCALES[locale], {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
    [locale],
  );

  const unread = app.conversations.reduce((s, c) => s + (c.unread?.[app.uid ?? ''] ?? 0), 0);

  return (
    <div className="screen">
      <div className="spread" style={{ marginBottom: 14 }}>
        <div>
          <div className="h1">
            {t(greetingKey(new Date().getHours()))}
            {app.user ? `, ${app.user.displayName}` : ''}
          </div>
          <div className="tiny faint">{dateLabel}</div>
        </div>
        <Link to="/profile" aria-label={t('tabs.profile')}>
          <div className="avatar">{initialsOf(app.user?.displayName ?? '?')}</div>
        </Link>
      </div>

      {/* ---- Today, both traditions. Deterministic, instant, offline. ---- */}
      <Section title={t('home.today')} term="gochara">
        <div className="col">
          {readings.map((r) => (
            <div key={r.system} className="card" data-mode={r.system}>
              <div className="spread" style={{ marginBottom: 8 }}>
                <span className="mode-chip">{t(SYSTEM_LABEL[r.system])}</span>
                <Link
                  to={`/horoscope?system=${r.system}`}
                  className="tiny"
                  style={{ color: 'var(--mode-ink)' }}
                >
                  {t('home.fullReading')} →
                </Link>
              </div>
              <div className="mode-rule" style={{ marginBottom: 10 }} />
              <div className="small" style={{ lineHeight: 1.55, marginBottom: 10 }}>
                {r.headline}
              </div>
              <div className="col">
                {r.strongest.slice(0, SIGNALS_ON_HOME).map((s) => (
                  <div key={s.id}>
                    <div className="tiny" style={{ color: 'var(--mode-ink)', fontWeight: 600 }}>
                      {s.label}
                      {s.term && <InfoDot term={s.term} />}
                    </div>
                    <div className="tiny muted" style={{ lineHeight: 1.5 }}>
                      {s.meaning}
                    </div>
                  </div>
                ))}
                {r.strongest.length === 0 && (
                  <div className="tiny muted">{t('horoscope.quietDay')}</div>
                )}
              </div>
              {r.sadeSati && (
                <div className="tiny faint" style={{ marginTop: 8 }}>
                  {t('horoscope.sadeSati')}
                  <InfoDot term="sade-sati" />
                </div>
              )}
            </div>
          ))}

          {/* Honest about what an unknown birth time costs, instead of a
              silently-guessed Vedic reading anchored on a coin flip. */}
          {app.chart && !moon.certain && (
            <div className="card" data-mode="vedic">
              <span className="mode-chip">{t('system.vedic')}</span>
              <div className="small muted" style={{ lineHeight: 1.55, margin: '10px 0' }}>
                {t('kundli.moonUncertain')}
              </div>
              <Link to="/profile/refine">
                <button className="btn btn--ghost btn--small btn--block">
                  {t('chart.refine.cta')} →
                </button>
              </Link>
            </div>
          )}

          {!app.chart && <div className="card small muted">{t('kundli.homeCalculating')}</div>}

          {/* The one paid thing on this screen, and only on a tap. */}
          <div className="card">
            <div className="tiny faint" style={{ marginBottom: 8 }}>
              {t('home.narrateHint')}
            </div>
            {insight ? (
              <>
                <div className="chat">
                  <Bubble
                    entry={insight}
                    onAction={app.actOnRecommendation}
                    openSources={openSources}
                    setOpenSources={setOpenSources}
                    factLookup={(id) => app.facts.find((f) => f.id === id)}
                  />
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <Link to="/ai" className="small accent">
                    {t('home.openChat')} →
                  </Link>
                  {/* A reply that cites nothing is almost always the friendly
                      error copy, and pinning that for a whole day is worse than
                      offering one deliberate re-run. */}
                  {(insight.citedFactIds?.length ?? 0) === 0 && (
                    <button className="chip" onClick={retryToday}>
                      {t('common.retry')}
                    </button>
                  )}
                </div>
              </>
            ) : reading ? (
              <div className="row">
                <div className="spinner" />
                <span className="muted small">{t('kundli.aiReading')}</span>
              </div>
            ) : (
              <button
                className="btn btn--ghost btn--small btn--block"
                onClick={readToday}
                disabled={app.allyBusy}
              >
                {t('home.readToday')}
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* ---- Your kundli, on the daily screen. The chart itself is the
             interface: every bhava compartment is tappable, and the "?" chip
             adds an invitation dot to each one. Reuses NorthIndianChart with
             the D1 varga the engine already computed — a lookup, not a
             calculation. A window-mode chart has no Lagna for the diamond to
             rotate around, so Home then says so and links to Charts, where
             the stability views tell the honest version of the story. ---- */}
      {app.chart && (
        <Section title={t('kundli.homeTitle')} term="kundali">
          {homeKundli ? (
            <div className="col" data-mode="vedic">
              <NorthIndianChart varga={homeKundli} />
              <div className="tiny muted center" style={{ lineHeight: 1.5 }}>
                {t('kundli.homeHintPre')}
                <Term id="bhava">bhava</Term>
                {t('kundli.homeHintPost')}
              </div>
              <Link to="/charts" className="small accent center">
                {t('kundli.homeOpen')} →
              </Link>
            </div>
          ) : (
            <div className="card" data-mode="vedic">
              <div className="small muted" style={{ lineHeight: 1.55, marginBottom: 10 }}>
                {t('kundli.homeNoLagna')}
              </div>
              <Link to="/charts">
                <button className="btn btn--ghost btn--small btn--block">
                  {t('kundli.homeOpen')} →
                </button>
              </Link>
            </div>
          )}
        </Section>
      )}

      {/* ---- Yesterday's verdict. The accuracy loop's only entry point. ---- */}
      {rateAsk && (
        <Section title={t('home.rateTitle')}>
          <div className="card">
            <div className="tiny faint" style={{ marginBottom: 6 }}>
              {t('kundli.rateYesterday')}
            </div>
            <div className="small" style={{ lineHeight: 1.55, fontWeight: 600 }}>
              {rateAsk.signalLabel}
            </div>
            <div className="small muted" style={{ lineHeight: 1.55, marginTop: 4 }}>
              {rateAsk.signalMeaning}
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button
                className="btn btn--primary btn--small"
                style={{ flex: 1 }}
                onClick={() =>
                  closeRateAsk(() =>
                    navigate(
                      `/journal?rate=${encodeURIComponent(rateAsk.periodKey)}` +
                        `&system=${rateAsk.system}&signal=${encodeURIComponent(rateAsk.signalId)}`,
                    ),
                  )
                }
              >
                {t('home.rateCta')}
              </button>
              <button className="btn btn--ghost btn--small" onClick={() => closeRateAsk()}>
                {t('home.rateSkip')}
              </button>
            </div>
          </div>
        </Section>
      )}

      {/* ---- Today's panchanga. Pure almanac maths, no chart, no AI. ---- */}
      {almanac && (
        <Section title={t('home.panchanga')} term="panchanga">
          <div className="card" data-mode="vedic">
            <div className="spread" style={{ marginBottom: 8 }}>
              <span className="mode-chip">
                {almanac.panchanga.vaara.name} · {almanac.panchanga.vaara.englishName}
              </span>
              <span className="tiny faint">
                {t('home.sunrise')} {clock(almanac.windows.day.sunriseUtc, locale)}
              </span>
            </div>
            <div className="mode-rule" style={{ marginBottom: 10 }} />

            <div className="wrap">
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                {t('panchanga.tithi')}: {almanac.panchanga.tithi.name}
                <InfoDot term="tithi" />
              </span>
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                {t('panchanga.nakshatra')}: {almanac.panchanga.nakshatra.name}
                <InfoDot term="nakshatra" />
              </span>
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                {t('panchanga.yoga')}: {almanac.panchanga.yoga.name}
                <InfoDot term="yoga" />
              </span>
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                {t('panchanga.karana')}: {almanac.panchanga.karana.name}
              </span>
            </div>

            <div className="divider" style={{ margin: '10px 0' }} />
            <div className="spread">
              <span className="tiny">
                {t('panchanga.rahuKaal')}
                <InfoDot term="rahu-kaal" />
              </span>
              <span className="tiny" style={{ color: 'var(--mode-ink)' }}>
                {clock(almanac.windows.rahuKaal.startUtc, locale)}–
                {clock(almanac.windows.rahuKaal.endUtc, locale)}
              </span>
            </div>
            {almanac.windows.abhijit && (
              <div className="spread" style={{ marginTop: 4 }}>
                <span className="tiny">{t('panchanga.abhijit')}</span>
                <span className="tiny" style={{ color: 'var(--mode-ink)' }}>
                  {clock(almanac.windows.abhijit.startUtc, locale)}–
                  {clock(almanac.windows.abhijit.endUtc, locale)}
                </span>
              </div>
            )}

            <Link to="/muhurta">
              <button
                className="btn btn--ghost btn--small btn--block"
                style={{ marginTop: 12 }}
              >
                {t('home.muhurtaCta')} →
              </button>
            </Link>
          </div>
        </Section>
      )}

      {/* ---- Continue your tree. Zero AI to render; only a tap costs. ---- */}
      {nextTree && (
        <Section title={t('home.treeTitle')}>
          <Link to={`/ai?tree=${nextTree.tree.id}`} style={{ color: 'inherit' }}>
            <div
              className="card"
              data-mode={
                nextTree.tree.system === 'vedic' || nextTree.tree.system === 'western'
                  ? nextTree.tree.system
                  : undefined
              }
            >
              <div className="spread" style={{ marginBottom: 6 }}>
                <div className="h3">{nextTree.tree.title}</div>
                <span className="badge badge--B">
                  {nextTree.progress
                    ? t('home.treeDepth', {
                        n: progressDepth(nextTree.tree, nextTree.progress.completedNodeIds),
                      })
                    : t('home.treeNew')}
                </span>
              </div>
              <div className="small muted" style={{ lineHeight: 1.55 }}>
                {nextTree.tree.blurb}
              </div>
              <div className="tiny accent" style={{ marginTop: 8 }}>
                {nextTree.progress ? t('home.treeContinue') : t('home.treeStart')} →
              </div>
            </div>
          </Link>
        </Section>
      )}

      {/* ---- Keep learning. Local curriculum progress; nothing to generate.
             The card's own tap opens the CONTENTS, not the next lesson: Learn
             is a book, and a book opens at its table of contents. The next
             unfinished lesson is still offered — as the second line, for the
             reader who does want to be walked forward. Two sibling links
             rather than one wrapping the other, because nested anchors are
             invalid and swallow the inner tap. ---- */}
      {keepLearning && (
        <Section title={keepLearning.done > 0 ? t('home.keepLearning') : t('home.startLearning')}>
          <div className="card">
            <Link to="/learn" style={{ color: 'inherit', display: 'block' }}>
              <div className="spread" style={{ marginBottom: 6 }}>
                <div className="h3">
                  {keepLearning.done > 0 ? t('learn.title') : t('home.startLearningTitle')}
                </div>
                <span className="tiny faint" style={{ flexShrink: 0 }}>
                  {t('learn.progress', { done: keepLearning.done, total: LESSON_COUNT })}
                </span>
              </div>
              <div className="tiny muted" style={{ lineHeight: 1.5 }}>
                {keepLearning.done > 0 ? t('learn.anyOrder') : t('home.startLearningBody')}
              </div>
              <div className="tiny accent" style={{ marginTop: 8 }}>
                {t('learn.contents')} →
              </div>
            </Link>

            {keepLearning.next && (
              <>
                <div className="divider" style={{ margin: '12px 0' }} />
                <Link
                  to={`/learn/${keepLearning.next.lesson.id}`}
                  style={{ color: 'inherit', display: 'block' }}
                >
                  <div className="spread">
                    <span style={{ minWidth: 0 }}>
                      <span className="tiny faint" style={{ display: 'block' }}>
                        {keepLearning.done > 0 ? t('learn.continue') : t('learn.start')}
                      </span>
                      <span className="small" style={{ display: 'block', lineHeight: 1.45 }}>
                        {keepLearning.next.lesson.title}
                      </span>
                    </span>
                    <span aria-hidden className="accent" style={{ flexShrink: 0 }}>
                      →
                    </span>
                  </div>
                </Link>
              </>
            )}
          </div>
        </Section>
      )}

      {/* ---- One tap to every surface that does not own a tab. ---- */}
      <Section title={t('home.explore')}>
        <div className="wrap">
          <Link to="/horoscope" className="chip">
            {t('nav.horoscope')}
          </Link>
          <Link to="/journal" className="chip">
            {t('nav.journal')}
          </Link>
          <Link to="/compatibility" className="chip">
            {t('nav.compatibility')}
          </Link>
          <Link to="/muhurta" className="chip">
            {t('nav.muhurta')}
          </Link>
          <Link to="/glossary" className="chip">
            {t('glossary.title')}
          </Link>
        </div>
      </Section>

      {lastAlly && lastAlly.ts !== insight?.ts && (
        <Section title={t('home.continue')}>
          <Link to="/ai" style={{ color: 'inherit' }}>
            <div className="card">
              <div className="tiny faint" style={{ marginBottom: 6 }}>
                {t('home.continueBody')}
              </div>
              <div className="small muted" style={{ lineHeight: 1.55 }}>
                {truncate(lastAlly.text, CONTINUE_PREVIEW)}
              </div>
              <div className="tiny accent" style={{ marginTop: 8 }}>
                {t('tabs.ai')} →
              </div>
            </div>
          </Link>
        </Section>
      )}

      <Section title={t('home.ask')}>
        <div className="wrap">
          {suggestions.map((q) => (
            <button key={q} className="chip" onClick={() => askAndOpen(q)}>
              {q}
            </button>
          ))}
        </div>
      </Section>

      {/* Deliberately no credit balance or tier chip here. Day-to-day use of
          the app never spends credits, so a wallet on the daily screen framed
          it as pay-per-use. Both live in Profile; the cost of a paid tier is
          shown at the moment of spending it, which is the only time it
          matters. */}

      {!socialOn && (
        <Section title={t('social.status.off')}>
          <div className="card">
            <div className="row" style={{ marginBottom: 8 }}>
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                🔒 {t('social.private.badge')}
              </span>
            </div>
            <div className="small muted" style={{ lineHeight: 1.55, marginBottom: 10 }}>
              {t('social.private.body')}
            </div>
            <Link to="/social/enable">
              <button className="btn btn--ghost btn--small btn--block">
                {t('social.private.cta')} →
              </button>
            </Link>
          </div>
        </Section>
      )}

      {aligned.length > 0 && (
        <Section title={t('home.people')}>
          <div className="col">
            {aligned.map((card) => (
              <Link key={card.uid} to={`/discover/${card.uid}`} style={{ color: 'inherit' }}>
                <div className="card row">
                  <div className="avatar avatar--gold">{initialsOf(card.displayName)}</div>
                  <div style={{ flex: 1 }}>
                    <div className="h3">{card.displayName}{card.age ? `, ${card.age}` : ''}</div>
                    <div className="tiny muted">
                      {card.compatibility.highlights.join(' · ') || card.interests.slice(0, 3).join(', ')}
                    </div>
                  </div>
                  <span className="badge badge--B">{card.compatibility.overall}</span>
                </div>
              </Link>
            ))}
            <Link to="/discover" className="small accent center">{t('home.seeMore')} →</Link>
          </div>
        </Section>
      )}

      {(app.incomingConnections.length > 0 || unread > 0) && (
        <Section title={t('home.requests')}>
          <Link to="/messages" style={{ color: 'inherit' }}>
            <div className="card spread">
              <span className="small">
                {app.incomingConnections.length > 0 &&
                  t('home.pendingRequests', { n: app.incomingConnections.length })}
                {app.incomingConnections.length > 0 && unread > 0 && ' · '}
                {unread > 0 && t('home.unreadMessages', { n: unread })}
              </span>
              <span className="accent">→</span>
            </div>
          </Link>
        </Section>
      )}

      <div className="tiny faint" style={{ marginTop: 18, lineHeight: 1.5 }}>
        {t('safety.disclaimer')}
      </div>
    </div>
  );
}
